import { ReactElement } from 'react';
import { FormFieldSelect } from '@client/common/components/Form/FormFieldSelect';
import { FORM_FIELD_TEXT_TYPE, FormFieldText } from '@client/common/components/Form/FormFieldText';
import { FormFieldTextArea } from '@client/common/components/Form/FormFieldTextArea';
import { getErrorMessage } from '@client/common/utils/form/form';
import { useTranslate } from '@client/i18n/hooks';
import { useFormikContext } from 'formik';
import { Alert, ALERT_DISPLAY_TYPE, ALERT_STYLE_TYPE } from 'b2b-components/theme/components/Alert';
import { Button, BUTTON_SIZE, BUTTON_STYLE_TYPE, BUTTON_TYPE } from 'b2b-components/theme/components/Button';
import { getEnumEntries } from 'b2b-components/utils/enum';
import { REPORT_PRODUCT_ERROR_SUBJECT_TYPE } from 'b2b-common/shop/product/api/Product.types';
import { FIELD_NAME, FormValuesType } from '../index';
import { Props } from './index';
import './Body.scss';

export const Body = ({
  onCancel,
  isSubmitting,
  hasSubmissionFail,
}: Props): ReactElement => {
  const { handleSubmit, errors, touched } = useFormikContext<FormValuesType>();
  const t = useTranslate();
  const text = {
    category: t('Category'),
    description: t('Description'),
    email: t('Your email (optional)'),
    reportIncorrectData: t('Report incorrect data'),
    cancel: t('Cancel'),
    send: t('Send'),
    productDescription: t('Product description'),
    productDocuments: t('Product documents'),
    scopeOfDelivery: t('Scope of delivery'),
    productImages: t('Product images'),
    productVideos: t('Product videos'),
    productSpecification: t('Product specification'),
    productAccessories: t('Product accessories'),
    other: t('Other'),
    error: t('There was an error sending the form. Please try again'),
  };

  return (
    <form onSubmit={handleSubmit} className="ProductReportForm">
      <h5 className="ProductReportForm-FormHeader">{text.reportIncorrectData}</h5>

      <div>
        <FormFieldSelect
          name={FIELD_NAME.subject}
          innerComponentProps={{
            error: getErrorMessage(
              errors[FIELD_NAME.subject],
              touched[FIELD_NAME.subject],
            ),
            text: {
              label: text.category,
            },
            options: getEnumEntries(REPORT_PRODUCT_ERROR_SUBJECT_TYPE).map(([key, value]) => ({
              label: text[key],
              value,
            })),
            isDisabled: isSubmitting,
            dataAttributes: { 'data-testid': 'pdpReportIncorrectDataCategorySelect' },
          }}
        />

        <FormFieldTextArea
          name={FIELD_NAME.text}
          innerComponentProps={{
            error: getErrorMessage(
              errors[FIELD_NAME.text],
              touched[FIELD_NAME.text],
            ),
            text: {
              label: text.description,
            },
            rows: 5,
            maxLength: 1000,
            isDisabled: isSubmitting,
          }}
        />

        <FormFieldText
          name={FIELD_NAME.email}
          innerComponentProps={{
            error: getErrorMessage(
              errors[FIELD_NAME.email],
              touched[FIELD_NAME.email],
            ),
            text: {
              label: text.email,
            },
            type: FORM_FIELD_TEXT_TYPE.email,
            disabled: isSubmitting,
          }}
        />
      </div>

      {hasSubmissionFail && (
        <Alert
          styleType={ALERT_STYLE_TYPE.error}
          displayType={ALERT_DISPLAY_TYPE.text}
        >
          {text.error}
        </Alert>
      )}

      <div className="ProductReportForm-Buttons">
        <Button
          onClick={onCancel}
          styleType={BUTTON_STYLE_TYPE.secondary}
          size={BUTTON_SIZE.small}
          isDisabled={isSubmitting}
          dataAttributes={{ 'data-testid': 'pdpReportIncorrectDataCancelButton' }}
        >
          {text.cancel}
        </Button>

        <Button
          type={BUTTON_TYPE.submit}
          size={BUTTON_SIZE.small}
          isDisabled={isSubmitting}
          dataAttributes={{ 'data-testid': 'pdpReportIncorrectDataSendButton' }}
        >
          {text.send}
        </Button>
      </div>
    </form>
  );
};
